﻿// ── Per-Tab Sidebar Configuration ──
// Used by page.tsx to render the correct sidebar for each top-level tab

export const TAB_KEYS = ['introduction', 'video', 'application', 'certification', 'report', 'materials'] as const;
export type TabKey = (typeof TAB_KEYS)[number];

export const TAB_LABELS: Record<TabKey, string> = {
  video: '成果视频',
  introduction: '成果简介',
  application: '申报书',
  certification: '成果鉴定',
  report: '成果报告',
  materials: '支撑材料',
};

export const TAB_ICONS: Record<TabKey, string> = {
  video: 'ri-video-line',
  introduction: 'ri-file-text-line',
  application: 'ri-article-line',
  certification: 'ri-shield-check-line',
  report: 'ri-bar-chart-box-line',
  materials: 'ri-folder-2-line',
};

export interface TabSidebarConfig {
  sidebarItems: SidebarNavItem[];
  defaultSectionId?: string;
}

// ═══════ TAB SIDEBAR MAP ═══════
export const tabSidebarMap: Record<TabKey, TabSidebarConfig> = {
  // ── 成果简介 ──
  introduction: {
    sidebarItems: [
      {
        id: 'intro-group',
        title: '成果简介',
        type: 'group',
        defaultExpanded: true,
        items: [
          { id: 'intro-main', label: '成果简介', icon: 'ri-file-text-line' },
        ],
      },
    ],
    defaultSectionId: 'intro-main',
  },

  // ── 成果视频: single video ──
  video: {
    sidebarItems: [
      {
        id: 'vid-group',
        title: '成果视频',
        type: 'group',
        defaultExpanded: true,
        items: [
          { id: 'vid-overview', label: '成果总览视频', icon: 'ri-film-line' },
        ],
      },
    ],
    defaultSectionId: 'vid-overview',
  },

  // ── 申报书: group + singles ──
  application: {
    sidebarItems: [
      {
        id: 'app-form-group',
        title: '申报书',
        type: 'group',
        defaultExpanded: true,
        items: [
          { id: 'app-form', label: '申报书预览', icon: 'ri-file-list-3-line' },
          { id: 'app-awards', label: '成果曾获奖励', icon: 'ri-medal-line' },
          { id: 'app-timeline', label: '成果起止时间', icon: 'ri-calendar-line' },
        ],
      },
      { id: 'section-intro', title: '成果主要解决的教学问题', type: 'single', icon: 'ri-information-line' },
      { id: 'section-methods', title: '成果解决教学问题的方法', type: 'single', icon: 'ri-tools-line' },
      { id: 'section-innovation', title: '成果的创新点', type: 'single', icon: 'ri-sparkling-line' },
      { id: 'section-promotion', title: '成果的推广应用效果', type: 'single', icon: 'ri-share-circle-line' },
      { id: 'section-contributors', title: '主要完成人情况', type: 'single', icon: 'ri-user-settings-line' },
      { id: 'section-units', title: '主要完成单位情况', type: 'single', icon: 'ri-building-line' },
      { id: 'section-recommend', title: '推荐单位意见', type: 'single', icon: 'ri-thumb-up-line' },
    ],
    defaultSectionId: 'app-form',
  },

  // ── 成果鉴定 ──
  certification: {
    sidebarItems: [
      {
        id: 'cert-group',
        title: '成果鉴定',
        type: 'group',
        defaultExpanded: true,
        items: [
          { id: 'cert-assoc', label: '中国建设教育协会', icon: 'ri-shield-check-line' },
          { id: 'cert-edu', label: '江西省教育厅', icon: 'ri-government-line' },
        ],
      },
    ],
    defaultSectionId: 'cert-assoc',
  },

  // ── 支撑材料: 1 big group with 16 items ──
  materials: {
    sidebarItems: [
      {
        id: 'mat-group',
        title: '支撑材料',
        type: 'group',
        defaultExpanded: true,
        items: [
          { id: 'mat-awards', label: '成果曾获奖励', icon: 'ri-medal-line' },
          { id: 'mat-honors', label: '专业所获荣誉', icon: 'ri-award-line' },
          { id: 'mat-curriculum', label: '课程建设', icon: 'ri-book-open-line' },
          { id: 'mat-textbooks', label: '教材建设', icon: 'ri-stack-line' },
          { id: 'mat-platform', label: '平台建设', icon: 'ri-server-line' },
          { id: 'mat-faculty', label: '师资队伍建设', icon: 'ri-team-line' },
          { id: 'mat-party', label: '党建思政建设', icon: 'ri-flag-line' },
          { id: 'mat-reform-projects', label: '代表性教改项目', icon: 'ri-rocket-line' },
          { id: 'mat-industry-cooperation', label: '产教合作基地', icon: 'ri-exchange-line' },
          { id: 'mat-international', label: '国际化人才培养', icon: 'ri-earth-line' },
          { id: 'mat-excellence', label: '卓越人才培养', icon: 'ri-star-smile-line' },
          { id: 'mat-student-awards', label: '学生学科竞赛获奖', icon: 'ri-trophy-line' },
          { id: 'mat-student-innovation', label: '学生创新创业项目', icon: 'ri-lightbulb-line' },
          { id: 'mat-outstanding-graduates', label: '优秀毕业生典型', icon: 'ri-star-line' },
          { id: 'mat-enterprise-evaluation', label: '企业评价', icon: 'ri-feedback-line' },
          { id: 'mat-university-evaluation', label: '高校评价', icon: 'ri-building-2-line' },
          { id: 'mat-social-impact', label: '社会影响', icon: 'ri-newspaper-line' },
        ],
      },
    ],
    defaultSectionId: 'mat-honors',
  },

  // ── 成果报告: group with 6 items ──
  report: {
    sidebarItems: [
      {
        id: 'rep-group',
        title: '成果报告',
        type: 'group',
        defaultExpanded: true,
        items: [
          { id: 'rep-background', label: '成果背景及问题提出', icon: 'ri-history-line' },
          { id: 'rep-history', label: '专业建设历程', icon: 'ri-timeline-line' },
          { id: 'rep-approach', label: '解决问题的总体思路', icon: 'ri-route-line' },
          { id: 'rep-content', label: '成果主要内容与解决方法', icon: 'ri-file-list-line' },
          { id: 'rep-innovation', label: '主要创新点', icon: 'ri-flashlight-line' },
          { id: 'rep-promotion', label: '推广应用效果与反思', icon: 'ri-global-line' },
        ],
      },
    ],
    defaultSectionId: 'rep-background',
  },
};

export interface SidebarNavGroup {
  id: string;
  title: string;
  type: 'group';
  defaultExpanded?: boolean;
  items: { id: string; label: string; icon?: string }[];
}

export interface SidebarNavSingle {
  id: string;
  title: string;
  type: 'single';
  icon?: string;
}

export type SidebarNavItem = SidebarNavGroup | SidebarNavSingle;

export interface FileItem {
  name: string;
  fileType: 'pdf' | 'image' | 'video' | 'file';
  url: string;
}

export interface TextContentData {
  contentType: 'text';
  title: string;
  content: string;
}

export interface FilesContentData {
  contentType: 'files';
  title: string;
  files: FileItem[];
}

export type SectionContent = TextContentData | FilesContentData;

// ── Sidebar Navigation Structure (legacy) ──
export const sidebarNav: SidebarNavItem[] = [
  {
    id: 'supporting-materials',
    title: '支撑材料',
    type: 'group',
    defaultExpanded: true,
    items: [
      { id: 'mat-awards', label: '成果曾获奖励', icon: 'ri-medal-line' },
      { id: 'mat-honors', label: '专业所获荣誉', icon: 'ri-award-line' },
      { id: 'mat-curriculum', label: '课程建设', icon: 'ri-book-open-line' },
      { id: 'mat-textbooks', label: '教材建设', icon: 'ri-stack-line' },
      { id: 'mat-platform', label: '平台建设', icon: 'ri-server-line' },
      { id: 'mat-faculty', label: '师资队伍建设', icon: 'ri-team-line' },
      { id: 'mat-party', label: '党建思政建设', icon: 'ri-flag-line' },
      { id: 'mat-reform-projects', label: '代表性教改项目', icon: 'ri-rocket-line' },
      { id: 'mat-industry-cooperation', label: '产教合作基地', icon: 'ri-exchange-line' },
      { id: 'mat-international', label: '国际化人才培养', icon: 'ri-earth-line' },
      { id: 'mat-excellence', label: '卓越人才培养', icon: 'ri-star-smile-line' },
      { id: 'mat-student-awards', label: '学生学科竞赛获奖', icon: 'ri-trophy-line' },
      { id: 'mat-student-innovation', label: '学生创新创业项目', icon: 'ri-lightbulb-line' },
      { id: 'mat-outstanding-graduates', label: '优秀毕业生典型', icon: 'ri-star-line' },
      { id: 'mat-enterprise-evaluation', label: '企业评价', icon: 'ri-feedback-line' },
      { id: 'mat-university-evaluation', label: '高校评价', icon: 'ri-building-2-line' },
      { id: 'mat-social-impact', label: '社会影响', icon: 'ri-newspaper-line' },
    ],
  },
  {
    id: 'application-form',
    title: '申报书',
    type: 'group',
    items: [
      { id: 'app-form', label: '申报书预览', icon: 'ri-file-list-3-line' },
      { id: 'app-awards', label: '成果曾获奖励', icon: 'ri-medal-line' },
      { id: 'app-timeline', label: '成果起止时间', icon: 'ri-calendar-line' },
    ],
  },
  {
    id: 'section-intro',
    title: '成果主要解决的教学问题',
    type: 'single',
    icon: 'ri-information-line',
  },
  {
    id: 'section-methods',
    title: '成果解决教学问题的方法',
    type: 'single',
    icon: 'ri-tools-line',
  },
  {
    id: 'section-innovation',
    title: '成果的创新点',
    type: 'single',
    icon: 'ri-sparkling-line',
  },
  {
    id: 'section-promotion',
    title: '成果的推广应用效果',
    type: 'single',
    icon: 'ri-share-circle-line',
  },
  {
    id: 'section-contributors',
    title: '主要完成人情况',
    type: 'single',
    icon: 'ri-user-settings-line',
  },
  {
    id: 'section-units',
    title: '主要完成单位情况',
    type: 'single',
    icon: 'ri-building-line',
  },
  {
    id: 'section-recommend',
    title: '推荐单位意见',
    type: 'single',
    icon: 'ri-thumb-up-line',
  },
  {
    id: 'report',
    title: '成果报告',
    type: 'group',
    items: [
      { id: 'rep-background', label: '成果背景及问题提出', icon: 'ri-history-line' },
      { id: 'rep-history', label: '专业建设历程', icon: 'ri-timeline-line' },
      { id: 'rep-approach', label: '解决问题的总体思路', icon: 'ri-route-line' },
      { id: 'rep-content', label: '成果主要内容与解决方法', icon: 'ri-file-list-line' },
      { id: 'rep-innovation', label: '主要创新点', icon: 'ri-flashlight-line' },
      { id: 'rep-promotion', label: '推广应用效果与反思', icon: 'ri-global-line' },
    ],
  },
];

// ── Content Map (keyed by sidebar item id) ──

const mockFiles: FileItem[] = [
  { name: '相关文件材料（一）', fileType: 'pdf', url: '/最终版.pdf' },
  { name: '佐证材料汇编', fileType: 'pdf', url: '/最终版.pdf' },
  { name: '成果证明材料扫描件', fileType: 'image', url: '#' },
  { name: '现场照片集锦', fileType: 'image', url: '#' },
  { name: '成果展示视频', fileType: 'video', url: '#' },
];

// Real PDF attachment URL
const REAL_PDF_URL = '/最终版.pdf';

export const contentMap: Record<string, SectionContent> = {
  // ── 成果简介 ──
  'intro-main': {
    contentType: 'text',
    title: '成果简介',
    content: `<h3>成果简介</h3>
<p>随着我国"交通强国、铁路先行"和铁路"走出去"等战略持续推进，交通基础设施建设高质量发展对行业人才提出了全方位、多层次和国际化需求，人才培养体系面临着从量到质的转型升级。</p>
<p>根据《华东交通大学党委关于深入推进解放思想、深化改革、开放办学、加快发展的若干意见》（华交党发〔2013〕56号）要求，学校以培养轨道交通特色"下得去、吃得苦、留得住、干得好、上得来"的"五得"高素质复合型人才为目标，通过"学科赋能、需求牵引、交叉融合"等系列举措，探索出了轨道交通特色土建类一流专业建设新模式。</p>

<h3>核心路径</h3>
<p><strong>第一阶段：学科赋能</strong>——以"火车头"党建为引领，通过构建学科带头人兼任专业负责人的双责一体联动机制，搭建教学示范中心与全国重点实验室互通的教研平台，实施"导师领航、研本接力"的研学联动牵引育人。实现"双责一体固本 → 平台互通搭台 → 研学联动通脉"的学科育人新路径。</p>
<p><strong>第二阶段：需求牵引</strong>——依据产业需求重构专业方向，"校企+校际"联动开设"中铁国际"订单班和留学生班级对接国际需求，实施铁路特色班级选拔及"天佑学子培育计划"。形成"产业需求定方向 → 校企校际协同育人 → '新铁军'品牌铸魂培优"的育人链条。</p>
<p><strong>第三阶段：交叉融合</strong>——多学科交叉融通打造"交通+""土木+"等复合型专业，产教融合强化实训实践教学资源，科教融汇赋能实践教学。构建"多学科融通建专业 → 产教融合赋能实训 → 科教融汇聚力科创"的融合发展机制。</p>

<h3>建设成效</h3>
<p>经13年探索实践，6个专业入选国家一流本科专业建设点，5个通过工程教育认证。已建成国家级教学实验示范中心等国家级平台4个；建成全国高校黄大年式教师团队等国家级教研团队2个，国家级一流课程16门。</p>
<p>近五年，获"挑战杯"学术科技作品竞赛国家级特等奖2项、中国国际大学生创新大赛国家级金奖5项、詹天佑优秀班集体等集体荣誉4项、茅以升铁道希望之星等个人荣誉25项。</p>
<p>培养学生获国家铁路局等大型央企高度认可，获中国教育报等多家国家媒体报道。</p>

<img src="/intro-img.png" alt="成果简介示意图" style="max-width:100%;display:block;border-radius:8px;margin-top:24px;" />
`,
  },

  // ── 成果鉴定 ──
  'cert-assoc': {
    contentType: 'files',
    title: '中国建设教育协会',
    files: [
      { name: '中国建设教育协会鉴定', fileType: 'pdf', url: '/最终版.pdf' },
    ],
  },
  'cert-edu': {
    contentType: 'files',
    title: '江西省教育厅',
    files: [
      { name: '江西省教育厅鉴定', fileType: 'pdf', url: '/最终版.pdf' },
    ],
  },

  // ── 成果视频 ──
  'vid-overview': {
    contentType: 'files',
    title: '成果总览视频',
    files: [
      { name: '教学成果总览宣传片', fileType: 'video', url: '#' },
    ],
  },

  // ── 支撑材料 ──
  'mat-awards': {
    contentType: 'files',
    title: '成果曾获奖励',
    files: [
      { name: '成果曾获奖励', fileType: 'pdf', url: '/1.pdf' },
    ],
  },
  'mat-honors': {
    contentType: 'files',
    title: '专业所获荣誉',
    files: [
      { name: '专业所获荣誉', fileType: 'pdf', url: '/2.pdf' },
    ],
  },
  'mat-curriculum': {
    contentType: 'files',
    title: '课程建设',
    files: [
      { name: '课程建设', fileType: 'pdf', url: '/3.pdf' },
    ],
  },
  'mat-textbooks': {
    contentType: 'files',
    title: '教材建设',
    files: [
      { name: '教材建设', fileType: 'pdf', url: '/4.pdf' },
    ],
  },
  'mat-platform': {
    contentType: 'files',
    title: '平台建设',
    files: [
      { name: '平台建设', fileType: 'pdf', url: '/5.pdf' },
    ],
  },
  'mat-faculty': {
    contentType: 'files',
    title: '师资队伍建设',
    files: [
      { name: '师资队伍建设', fileType: 'pdf', url: '/6.pdf' },
    ],
  },
  'mat-party': {
    contentType: 'files',
    title: '党建思政建设',
    files: [
      { name: '党建思政建设', fileType: 'pdf', url: '/7.pdf' },
    ],
  },
  'mat-reform-projects': {
    contentType: 'files',
    title: '代表性教改项目',
    files: [
      { name: '代表性教改项目', fileType: 'pdf', url: '/8.pdf' },
    ],
  },
  'mat-industry-cooperation': {
    contentType: 'files',
    title: '产教合作基地',
    files: [
      { name: '产教合作基地', fileType: 'pdf', url: '/9.pdf' },
    ],
  },
  'mat-international': {
    contentType: 'files',
    title: '国际化人才培养',
    files: [
      { name: '国际化人才培养', fileType: 'pdf', url: '/10.pdf' },
    ],
  },
  'mat-excellence': {
    contentType: 'files',
    title: '卓越人才培养',
    files: [
      { name: '卓越人才培养', fileType: 'pdf', url: '/11.pdf' },
    ],
  },
  'mat-student-awards': {
    contentType: 'files',
    title: '学生学科竞赛获奖',
    files: [
      { name: '学生学科竞赛获奖', fileType: 'pdf', url: '/12.pdf' },
    ],
  },
  'mat-student-innovation': {
    contentType: 'files',
    title: '学生创新创业项目',
    files: [
      { name: '学生创新创业项目', fileType: 'pdf', url: '/13.pdf' },
    ],
  },
  'mat-outstanding-graduates': {
    contentType: 'files',
    title: '优秀毕业生典型',
    files: [
      { name: '优秀毕业生典型', fileType: 'pdf', url: '/14.pdf' },
    ],
  },
  'mat-enterprise-evaluation': {
    contentType: 'files',
    title: '企业评价',
    files: [
      { name: '企业评价', fileType: 'pdf', url: '/15.pdf' },
    ],
  },
  'mat-university-evaluation': {
    contentType: 'files',
    title: '高校评价',
    files: [
      { name: '高校评价', fileType: 'pdf', url: '/16.pdf' },
    ],
  },
  'mat-social-impact': {
    contentType: 'files',
    title: '社会影响',
    files: [
      { name: '社会影响', fileType: 'pdf', url: '/17.pdf' },
    ],
  },

  // ── 申报书 ──
  'app-form': {
    contentType: 'files',
    title: '申报书预览',
    files: [
      { name: '申报书封面及基本信息', fileType: 'pdf', url: REAL_PDF_URL },
    ],
  },
  'app-awards': {
    contentType: 'files',
    title: '成果曾获奖励',
    files: [
      { name: '国家级教学成果二等奖证书', fileType: 'pdf', url: REAL_PDF_URL },
    ],
  },
  'app-timeline': {
    contentType: 'text',
    title: '成果起止时间',
    content: `<h3>成果起止时间</h3>
<p><strong>起始时间：</strong>2020年1月</p>
<p><strong>完成时间：</strong>2026年6月</p>
<p><strong>历时：</strong>六年半</p>
<h3>主要时间节点</h3>
<p><strong>第一阶段（2020.01 - 2020.12）：调研与方案设计</strong>——开展轨道交通行业人才需求调研，走访中国中车、中国铁建等12家龙头企业，形成调研报告；完成产教融合人才培养方案初稿设计。</p>
<p><strong>第二阶段（2021.01 - 2022.06）：试点实施</strong>——在轨道交通信号与控制、车辆工程2个专业率先试点，建成校内"产业微环境"实训基地3个，签订校企合作协议6份。</p>
<p><strong>第三阶段（2022.07 - 2024.06）：全面推广</strong>——成果扩展至6个轨道交通相关专业，建成校企共建实训基地12个，开发校企合作课程15门，"双师型"教师比例提升至78%。</p>
<p><strong>第四阶段（2024.07 - 2026.06）：总结提升</strong>——凝练成果特色，形成可复制推广的经验模式；成果被8所高校借鉴应用，受益学生超20000人；完成成果鉴定和申报工作。</p>`,
  },

  // ... rest of the content remains the same
  // ── 申报书 - 教学问题 ──
  'section-intro': {
    contentType: 'files',
    title: '成果主要解决的教学问题',
    files: [
      { name: '成果主要解决的教学问题', fileType: 'pdf', url: '/app-problem.pdf' },
    ],
  },

  // ── 申报书 - 解决方法 ──
  'section-methods': {
    contentType: 'files',
    title: '成果解决教学问题的方法',
    files: [
      { name: '成果解决教学问题的方法', fileType: 'pdf', url: '/app-methods.pdf' },
    ],
  },

  // ── 申报书 - 创新点 ──
  'section-innovation': {
    contentType: 'files',
    title: '成果的创新点',
    files: [
      { name: '成果的创新点', fileType: 'pdf', url: '/app-innovation.pdf' },
    ],
  },

  // ── 申报书 - 推广应用效果 ──
  'section-promotion': {
    contentType: 'files',
    title: '成果的推广应用效果',
    files: [
      { name: '成果的推广应用效果', fileType: 'pdf', url: '/app-promotion.pdf' },
    ],
  },

  // ── 主要完成人情况 ──
  'section-contributors': {
    contentType: 'files',
    title: '主要完成人情况',
    files: [
      { name: '主要完成人情况', fileType: 'pdf', url: REAL_PDF_URL },
    ],
  },

  // ── 主要完成单位情况 ──
  'section-units': {
    contentType: 'files',
    title: '主要完成单位情况',
    files: [
      { name: '主要完成单位情况', fileType: 'pdf', url: '/app-units.pdf' },
    ],
  },

  // ── 成果报告 ──
  'rep-background': {
    contentType: 'files',
    title: '成果背景及问题提出',
    files: [
      { name: '成果背景及问题提出', fileType: 'pdf', url: '/report-1-background.pdf' },
    ],
  },
  'rep-history': {
    contentType: 'files',
    title: '专业建设历程',
    files: [
      { name: '专业建设历程', fileType: 'pdf', url: '/report-2-history.pdf' },
    ],
  },
  'rep-approach': {
    contentType: 'files',
    title: '解决问题的总体思路',
    files: [
      { name: '解决问题的总体思路', fileType: 'pdf', url: '/report-3-approach.pdf' },
    ],
  },
  'rep-content': {
    contentType: 'files',
    title: '成果主要内容与解决方法',
    files: [
      { name: '成果主要内容与解决方法', fileType: 'pdf', url: '/report-4-content.pdf' },
    ],
  },
  'rep-innovation': {
    contentType: 'files',
    title: '主要创新点',
    files: [
      { name: '主要创新点', fileType: 'pdf', url: '/report-5-innovation.pdf' },
    ],
  },
  'rep-promotion': {
    contentType: 'files',
    title: '推广应用效果与反思',
    files: [
      { name: '推广应用效果与反思', fileType: 'pdf', url: '/report-6-promotion.pdf' },
    ],
  },

  // ── 推荐单位意见 ──
  'section-recommend': {
    contentType: 'files',
    title: '推荐单位意见',
    files: [
      { name: '推荐单位意见', fileType: 'pdf', url: '/app-recommendation.pdf' },
    ],
  },
};